import {
  INDIAN_POKER_ANTE,
  INDIAN_POKER_CARD_VALUES,
  INDIAN_POKER_MAX_PLAYERS,
  INDIAN_POKER_MIN_PLAYERS,
  INDIAN_POKER_MIN_STARTING_CHIPS,
  INDIAN_POKER_STARTING_CHIPS,
  type IndianPokerCard,
  type IndianPokerCardValue,
  type IndianPokerPlayer,
  type IndianPokerPriorityDraw,
  type IndianPokerRngState,
  type IndianPokerState,
  type InitGameOptions,
} from "./types";
import { createShuffledDeck } from "./utils/deck";
import { createInitialRngState, randomInt } from "./utils/seeded-random";

function assertValidInitOptions(options: InitGameOptions): void {
  if (
    options.playerIds.length < INDIAN_POKER_MIN_PLAYERS ||
    options.playerIds.length > INDIAN_POKER_MAX_PLAYERS
  ) {
    throw new Error(
      `Indian Poker requires exactly ${INDIAN_POKER_MIN_PLAYERS} players`,
    );
  }

  const deduped = new Set(options.playerIds);
  if (deduped.size !== options.playerIds.length) {
    throw new Error("Player IDs must be unique");
  }

  if (!deduped.has(options.hostPlayerId)) {
    throw new Error("Host player must be included in player IDs");
  }

  if (options.seed.trim().length === 0) {
    throw new Error("Seed is required");
  }
}

function toCardValue(value: number): IndianPokerCardValue {
  const cardValue = INDIAN_POKER_CARD_VALUES.find(
    (candidate) => candidate === value,
  );

  if (cardValue === undefined) {
    throw new Error(`Invalid Indian Poker card value: ${value}`);
  }

  return cardValue;
}

function determineInitialPriority(
  playerIds: readonly string[],
  rngState: IndianPokerRngState,
): {
  readonly draws: readonly IndianPokerPriorityDraw[];
  readonly priorityPlayerId: string;
  readonly rngState: IndianPokerRngState;
} {
  const firstPlayerId = playerIds[0];
  const secondPlayerId = playerIds[1];
  if (firstPlayerId === undefined || secondPlayerId === undefined) {
    throw new Error("Indian Poker requires two players");
  }

  let currentRngState = rngState;

  while (true) {
    const firstDraw = randomInt(
      currentRngState,
      INDIAN_POKER_CARD_VALUES.length,
    );
    currentRngState = firstDraw.rngState;
    const secondDraw = randomInt(
      currentRngState,
      INDIAN_POKER_CARD_VALUES.length,
    );
    currentRngState = secondDraw.rngState;

    const firstValue = toCardValue(firstDraw.value + 1);
    const secondValue = toCardValue(secondDraw.value + 1);

    if (firstValue === secondValue) {
      continue;
    }

    return {
      draws: [
        { playerId: firstPlayerId, value: firstValue },
        { playerId: secondPlayerId, value: secondValue },
      ],
      priorityPlayerId:
        firstValue > secondValue ? firstPlayerId : secondPlayerId,
      rngState: currentRngState,
    };
  }
}

function drawCard(deck: readonly IndianPokerCard[]): {
  readonly card: IndianPokerCard;
  readonly deck: readonly IndianPokerCard[];
} {
  const [card, ...remainingDeck] = deck;
  if (card === undefined) {
    throw new Error("Cannot draw from an empty deck");
  }

  return {
    card,
    deck: remainingDeck,
  };
}

function createInitialPlayers(
  playerIds: readonly string[],
  startingChips: number,
): readonly IndianPokerPlayer[] {
  return playerIds.map((playerId) => ({
    playerId,
    chips: startingChips - INDIAN_POKER_ANTE,
    currentBet: INDIAN_POKER_ANTE,
  }));
}

function resolveStartingChips(startingChips: number | undefined): number {
  if (startingChips === undefined) {
    return INDIAN_POKER_STARTING_CHIPS;
  }

  if (!Number.isFinite(startingChips)) {
    throw new Error("Starting chips must be a finite number");
  }

  const normalized = Math.trunc(startingChips);
  if (normalized < INDIAN_POKER_MIN_STARTING_CHIPS) {
    throw new Error(
      `Starting chips must be at least ${INDIAN_POKER_MIN_STARTING_CHIPS}`,
    );
  }

  return normalized;
}

function dealInitialCards(
  playerIds: readonly string[],
  deck: readonly IndianPokerCard[],
): {
  readonly cardsByPlayerId: Readonly<Record<string, IndianPokerCard>>;
  readonly deck: readonly IndianPokerCard[];
} {
  const firstDeal = drawCard(deck);
  const secondDeal = drawCard(firstDeal.deck);
  const firstPlayerId = playerIds[0];
  const secondPlayerId = playerIds[1];

  if (firstPlayerId === undefined || secondPlayerId === undefined) {
    throw new Error("Indian Poker requires two players");
  }

  return {
    cardsByPlayerId: {
      [firstPlayerId]: firstDeal.card,
      [secondPlayerId]: secondDeal.card,
    },
    deck: secondDeal.deck,
  };
}

export function initGame(options: InitGameOptions): IndianPokerState {
  assertValidInitOptions(options);

  const startingChips = resolveStartingChips(options.startingChips);
  const initialRngState = createInitialRngState(options.seed);
  const priority = determineInitialPriority(options.playerIds, initialRngState);
  const shuffled = createShuffledDeck(1, priority.rngState);
  const deal = dealInitialCards(options.playerIds, shuffled.deck);
  const pot = options.playerIds.length * INDIAN_POKER_ANTE;

  return {
    gameId: options.gameId ?? `indian-poker-${options.playerIds.join("-")}`,
    status: "IN_PROGRESS",
    hostPlayerId: options.hostPlayerId,
    players: createInitialPlayers(options.playerIds, startingChips),
    round: {
      roundNumber: 1,
      phase: "BETTING",
      activePlayerId: priority.priorityPlayerId,
      bettingPriorityPlayerId: priority.priorityPlayerId,
      pot,
      carryoverPot: 0,
      actedPlayerIds: [],
      cardsByPlayerId: deal.cardsByPlayerId,
      confirmedPlayerIds: [],
    },
    completedRounds: [],
    deck: deal.deck,
    discardPile: [],
    deckGeneration: 1,
    rngState: shuffled.rngState,
    initialPriorityDraws: priority.draws,
  };
}
